import { defaultOperatorName } from '@/utils/constants'

const NLP_PARAMETERS = '&confidence=0.6&match=exact&endpoint=DBPEDIA&limit=100&window=8&format=simplified&keywords=regex&mwe_pipeline=strict'

export class SearchEngine {
  constructor (name) {
    this.name = name
  }
  setAddQuotesAroundPhrases (addQuotesAroundPhrases) {
    this.addQuotesAroundPhrases = addQuotesAroundPhrases
  }
  mapOperator (operator) {
    return { op: operator, spaces: true }
  }
  isPrefixOperator (operator) {
    return operator === 'NOT'
  }
  resetQueryParameters () {
    // To be overridden in subclasses
  }
  formulateQueryFromNodeData (nodeData) {
    this.resetQueryParameters()
    const baseTerm = nodeData.text.replace(SearchEngine.quoteReplaceRE, '\\"')
    const addQuotes = this.addQuotesAroundPhrases && baseTerm && baseTerm.indexOf(' ') > 0
    return this.formTerm(baseTerm, nodeData.isNegative, addQuotes,
      nodeData.fieldSpecifier)
  }
  formTerm (text, isNegative, addQuotes, fieldSpecifier) {
    let r = ''
    if (isNegative) {
      r += '-'
    }
    if (fieldSpecifier && fieldSpecifier.length > 0) {
      r += fieldSpecifier + ':'
    }
    if (addQuotes) {
      r += '"'
    }
    r += text
    if (addQuotes) {
      r += '"'
    }
    return encodeURIComponent(r)
  }
  formulateQueryFromTreeData (treeData) {
    this.resetQueryParameters()
    return this.formulateQueryFromTreeDataInternal(treeData)
  }
  formulateQueryFromTreeDataInternal (treeData, parentFieldSpecifier) {
    if (typeof treeData.children === 'undefined') {
      return this.formTerm(treeData.text)
    }
    let r = ''
    let isFirst = true
    const thisOperator = treeData.operator || defaultOperatorName
    const mappedThisOperator = this.mapOperator(thisOperator)
    const thisChildrenCount = treeData.children.length
    for (const i of treeData.children) {
      if (i.isOperator) {
        continue
      }
      let fieldSpecifier = null
      if (i.fieldSpecifier) {
        fieldSpecifier = i.fieldSpecifier
      } else if (parentFieldSpecifier) {
        fieldSpecifier = parentFieldSpecifier
      }
      const term = (i.children && i.children.length > 0)
        ? null
        : this.formTerm(i.text, i.isNegative, this.addQuotes(i), fieldSpecifier)
      if (term !== null && term.length <= 0) {
        continue
      }
      if (this.isPrefixOperator(thisOperator)) {
        r += mappedThisOperator.op
      } else if (!isFirst) {
        if (mappedThisOperator.spaces) {
          r += ' '
        }
        r += mappedThisOperator.op
        if (mappedThisOperator.spaces && mappedThisOperator.op !== '') {
          r += ' '
        }
      }
      if (isFirst) {
        isFirst = false
      }
      if (i.children && i.children.length > 0) {
        if (i.isNegative) {
          r += 'NOT '
        }
        const addBrackets = i.isNegative || thisChildrenCount > 1
        const bracketedTerm = this.formulateQueryFromTreeDataInternal(i, fieldSpecifier)
        if (bracketedTerm && bracketedTerm.length > 0) {
          if (addBrackets) {
            r += '('
          }
          r += bracketedTerm
          if (addBrackets) {
            r += ')'
          }
        }
      } else {
        r += term
      }
    }
    r = this.lastPass(r)
    return r
  }
  addQuotes (node) {
    return this.addQuotesAroundPhrases && node && node.text && node.text.indexOf(' ') > 0
  }
  lastPass (r) {
    return r
  }
  scanGraphQueryData (qd, filter) {
    if (typeof qd.children !== 'undefined') {
      qd.children.forEach(c => {
        this.scanGraphQueryData(c, filter)
      })
    }
    filter(qd)
  }
  getFieldSpecifiers (qd) {
    const r = new Set()
    this.scanGraphQueryData(qd, partData => {
      if (typeof partData.fieldSpecifier !== 'undefined') {
        r.add(partData.fieldSpecifier)
      }
    })
    return r
  }
  countTerms (qd) {
    let r = 0
    this.scanGraphQueryData(qd, partData => {
      if (typeof partData.isGroup === 'undefined' || !partData.isGroup) {
        r++
      }
    })
    return r
  }
  formulateDisplayQuery (query) {
    return decodeURIComponent(query)
  }
  lintFieldSpecifiers (queryGraphData, fieldSpecifiers) {
    const m = new Set()
    const graphFieldSpecifiers = this.getFieldSpecifiers(queryGraphData)
    const expectedLowerCase = fieldSpecifiers.map(x => x.name.toLowerCase())
    graphFieldSpecifiers.forEach(x => {
      if (!expectedLowerCase.includes(x.toLowerCase())) {
        m.add(x)
      }
    })
    let r = null
    if (m.size > 0) {
      r = {
        title: `<span class="warning-message-source">${this.name}</span> does <span class="warning-message-highlight">not support</span> the following <span class="warning-message-highlight">operators</span>:`,
        items: []
      }
      m.forEach(x => {
        r.items.push(x)
      })
    }
    return r
  }
  canGetCount () {
    return false
  }
  getCount (terms) {
    return null
  }
  getSuggestTermsMeshSettings () {
    return {
      sourceName: 'MeSH',
      sourceUrl: 'https://www.nlm.nih.gov/mesh/',
      methodName: 'Large Language Models',
      methodUrl: 'https://www.2dsearch.com/research'
    }
  }
  getSuggestTermsDbpediaSettings () {
    return {
      sourceName: 'DBPEDIA',
      sourceUrl: 'https://wiki.dbpedia.org/',
      methodName: 'Large Language Models',
      methodUrl: 'https://www.2dsearch.com/research'
    }
  }
  nlpParameters () {
    return NLP_PARAMETERS
  }
  formatErrorMessage (type, value = null, linkUrl = null) {
    switch (type) {
      case 'charLimit':
        return `${this.formattedNameForErrorMessage()} searching is limited to the <span class="warning-message-highlight">first ${value} characters</span>.`
      case 'termLimit':
        return `${this.formattedNameForErrorMessage()} searching is limited to the <span class="warning-message-highlight">first ${value} terms</span>.`
      case 'wordAndOperatorLimit':
        return `${this.formattedNameForErrorMessage()} searching is limited to the <span class="warning-message-highlight">first ${value} words including operators</span>.`
      case 'oneLevelNesting':
        return `${this.formattedNameForErrorMessage()} supports only <span class="warning-message-highlight">one level of nesting</span>.`
      case 'oneLevelSubexpressionNesting':
        return `${this.formattedNameForErrorMessage()} does <span class="warning-message-highlight">not support nesting</span> of logical subexpressions <span class="warning-message-highlight">deeper than one level</span>.`
      case 'trailingAsterisksTruncation':
        return `${this.formattedNameForErrorMessage()} does <span class="warning-message-highlight">not support searches</span> based on <span class="warning-message-highlight">truncation using trailing asterisks</span>.`
      case 'proximityOperator':
        return `${this.formattedNameForErrorMessage()} does <span class="warning-message-highlight">not support proximity operators</span> (${value}). See <a target="_blank" href="${linkUrl}">${this.name} help</a> for further details.`
      default:
        return `There is an error when using your strategy with ${this.formattedNameForErrorMessage()}.`
    }
  }
  formattedNameForErrorMessage () {
    return `<span class="warning-message-source">${this.name}</span>`
  }
}

export class BrowserSearchEngine extends SearchEngine {
  mapOperator (operator) {
    switch (operator) {
      case 'AND': return { op: '', spaces: true }
      case 'OR': return { op: 'OR', spaces: true }
      case 'NOT': return { op: '-', spaces: true }
    }
    return { op: operator, spaces: true }
  }
  getSuggestTermsMetaData () {
    return this.getSuggestTermsDbpediaSettings()
  }
}

Object.defineProperty(SearchEngine, 'quoteReplaceRE', {
  value: new RegExp('"', 'g')
})



// WEBPACK FOOTER //
// ./src/formulate_queries/common.js